/** Firebase Auth IndexedDB persistence can throw when the tab is hidden or RTDB is reconnecting. */
export function isBenignAuthPersistenceError(error: unknown): boolean {
    const msg = (error instanceof Error ? error.message : String(error)).toLowerCase();
    return msg.includes('closing/hidden')
        || msg.includes('database is closing')
        || msg.includes('indexed_db')
        || msg.includes("failed to execute 'transaction'");
}

/** Transient connectivity blips during background Auth token refresh (tab sleep, offline, throttled). */
export function isBenignAuthNetworkError(error: unknown): boolean {
    const code = getFirebaseAuthErrorCode(error);
    if (code === 'auth/network-request-failed') {
        return true;
    }
    const msg = (error instanceof Error ? error.message : String(error)).toLowerCase();
    return msg.includes('network-request-failed');
}

function getFirebaseAuthErrorCode(error: unknown): string | undefined {
    if (error != null && typeof error === 'object' && 'code' in error) {
        const code = (error as { code?: unknown }).code;
        return typeof code === 'string' ? code : undefined;
    }
    return undefined;
}

/**
 * Firebase Auth startup (`accounts:lookup`) can reject an internal promise with a plain `{}`
 * when the lookup request fails (iOS network blip, blocked request, stale session). Not actionable app logic.
 */
export function isBenignAuthEmptyObjectRejection(error: unknown): boolean {
    if (error == null || typeof error !== 'object' || Array.isArray(error) || error instanceof Error) {
        return false;
    }
    if (Object.keys(error as object).length > 0) {
        return false;
    }
    const proto = Object.getPrototypeOf(error);
    return proto === Object.prototype || proto === null;
}

/**
 * Known @firebase/auth signInWithPopup race (firebase-js-sdk #6923 / #7093): the popup-closed poller
 * clears `pendingPromise`, then the late auth event calls reject() and trips an internal debugAssert.
 * Sign-in has already succeeded by then (seen on iOS Chrome, where the popup is a tab switch).
 */
export function isBenignAuthPopupRaceError(error: unknown): boolean {
    const msg = (error instanceof Error ? error.message : String(error)).toLowerCase();
    return msg.includes('pending promise was never set');
}

export function isBenignAuthError(error: unknown): boolean {
    return isBenignAuthPersistenceError(error)
        || isBenignAuthNetworkError(error)
        || isBenignAuthPopupRaceError(error)
        || isBenignAuthEmptyObjectRejection(error);
}

let rejectionHandlerInstalled = false;

/** Swallow benign Auth IndexedDB rejections so they do not surface as uncaught promise errors. */
export function installBenignAuthPersistenceRejectionHandler(): void {
    if (rejectionHandlerInstalled || typeof window === 'undefined') {
        return;
    }
    rejectionHandlerInstalled = true;
    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
        if (isBenignAuthError(event.reason)) {
            event.preventDefault();
        }
    });
}
